import { addReactError } from '@datadog/browser-rum-react';
import { Button, Result } from 'antd';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

import { type ErrorContext, trackDatadogError } from '@/libs/datadog';

import * as Styled from './styles';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * When provided, a caught error is also reported to Datadog RUM with this
   * context attached, so a boundary that degrades to a fallback (rather than
   * the default error screen) stays filterable/alertable — e.g. by `feature`.
   */
  errorContext?: ErrorContext;
};

type State = {
  hasError: boolean;
};

class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false
  };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    addReactError(error, info);

    if (this.props.errorContext) {
      trackDatadogError(error, this.props.errorContext);
    }

    console.error({ error, info });
  }

  handleBackHome = () => {
    globalThis.location.href = '/';
  };

  render() {
    const { children, fallback } = this.props;

    if (!this.state.hasError) {
      return children;
    }

    return (
      fallback ?? (
        <Styled.Container>
          <div className='content'>
            <Result
              status='error'
              title='Oops! Something did not go as planned.'
              subTitle='Please refresh the page. If this keeps happening, contact support.'
              extra={
                <Button size='large' onClick={this.handleBackHome}>
                  Back Home
                </Button>
              }
            />
          </div>
        </Styled.Container>
      )
    );
  }
}

export default ErrorBoundary;
